import { Center, Loader } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';

import { restoreSession } from '../features/auth';
import { onSessionLost } from '../shared/api/client';
import { useSession, useSessionWriter } from '../shared/session';
import { LoginPage } from '../pages/LoginPage';

type Phase = 'restoring' | 'ready';

/**
 * Единственный обработчик класса «сессия» (AD-A3).
 *
 * На старте — восстановление из refresh во вкладке; `401`, переживший обновление, приходит
 * сюда сигналом, а не до экрана: хранилище и кэш чистятся, и человек видит вход с
 * пояснением. Начатая работа при этом бросается, а не переигрывается.
 */
export function SessionGate() {
  const [phase, setPhase] = useState<Phase>('restoring');
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const session = useSession();
  const writer = useSessionWriter();

  useEffect(() => {
    let cancelled = false;
    restoreSession()
      .then((restored) => {
        if (cancelled) {
          return;
        }
        if (restored) {
          writer.set(restored);
        }
      })
      .catch(() => {
        // Восстановление не удалось (нет права admin, ответ не по контракту): вход заново.
      })
      .finally(() => {
        if (!cancelled) {
          setPhase('ready');
        }
      });
    return () => {
      cancelled = true;
    };
    // Писатель стабилен на жизнь клиента запросов; повторный запуск восстановления — нет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () =>
      onSessionLost(() => {
        writer.clear();
        setNotice('Сессия завершилась. Войдите снова.');
      }),
    [writer],
  );

  if (phase === 'restoring') {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }
  if (!session) {
    return <LoginPage notice={notice} />;
  }
  return <Outlet />;
}
